import { setLocalStorageAndCookie, getLocalStorageOrCookie } from "./utils";

async function loadNewsletterModal() {
  const modalElement = document.querySelector("#newsletter-modal");
  const newsletterWrapper = modalElement?.querySelector(
    ".customized-newsletter",
  );

  const isExcludedPage =
    window.location.pathname === "/" ||
    window.location.pathname.includes("/news-and-insights");

  if (!newsletterWrapper || !modalElement || isExcludedPage) return;

  let isProcessing = false;
  const DAY = 24 * 60 * 60 * 1000;
  const SUBSCRIBE_CHECK_API_URL = "/Common/Membership/CheckIsUserSubscribed";
  const STORAGE_KEY = "ve.eu.themes.newslettermodal";

  // Initial UI Setup
  const emailInput = newsletterWrapper.querySelector(".form-control.Email");
  if (emailInput && typeof veDictionary !== "undefined") {
    emailInput.placeholder = veDictionary.email;
  }

  async function getUserData() {
    try {
      const response = await fetch(SUBSCRIBE_CHECK_API_URL);
      if (!response.ok) return { IsSubscribed: false };
      return await response.json();
    } catch (e) {
      return { IsSubscribed: false };
    }
  }

  const resetAllConditions = () => {
    const todayStr = new Date().toISOString().split("T")[0];

    const newState = {
      // 1. Keep the page count
      pageCount: 1,

      // 2. Set history to ONLY include today
      visitHistory: [todayStr],
    };

    setLocalStorageAndCookie(STORAGE_KEY, JSON.stringify(newState));
  };

  const userInfo = await getUserData();

  if (userInfo.IsSubscribed) return;

  const Config = {
    timeToShow: 20000,
    timeToShowOnNextPage: 15000,
    getState: function () {
      const newsletterData = getLocalStorageOrCookie(STORAGE_KEY);
      return JSON.parse(newsletterData || "{}");
    },

    setState: function (key, value) {
      let state = this.getState();
      state[key] = value;
      setLocalStorageAndCookie(STORAGE_KEY, JSON.stringify(state));
    },

    // --- VISIT TRACKING LOGIC ---
    recordVisit: function () {
      const state = this.getState();
      const history = state.visitHistory || []; // Array of strings like ["2026-02-09", "2026-02-10"]
      const todayStr = new Date().toISOString().split("T")[0];

      if (!history.includes(todayStr)) {
        history.push(todayStr);

        // Cleanup: Keep only visits from the last 30 days
        const thirtyDaysAgo = new Date(Date.now() - 30 * DAY)
          .toISOString()
          .split("T")[0];
        const cleanedHistory = history
          .filter((dateStr) => dateStr >= thirtyDaysAgo)
          .sort(); // String sorting works perfectly for YYYY-MM-DD

        this.setState("visitHistory", cleanedHistory);
      }
    },

    checkVisitRequirements: function () {
      const state = this.getState();
      const history = state.visitHistory || [];
      if (history.length < 3) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // 1. Check: 3 different days in past 7 days
      const sevenDaysAgoStr = new Date(today.getTime() - 7 * DAY)
        .toISOString()
        .split("T")[0];
      const visitsLast7Days = history.filter(
        (dateStr) => dateStr >= sevenDaysAgoStr,
      );
      const hasThreeInSeven = visitsLast7Days.length >= 3;

      // 2. Check: 3 consecutive days
      let consecutiveCount = 1;
      let hasThreeConsecutive = false;

      for (let i = 1; i < history.length; i++) {
        const prevDate = new Date(history[i - 1]);
        const currDate = new Date(history[i]);

        // Difference in time between current date and previous date
        const diff = currDate.getTime() - prevDate.getTime();

        if (diff === DAY) {
          consecutiveCount++;
        } else {
          consecutiveCount = 1;
        }

        if (consecutiveCount >= 3) {
          hasThreeConsecutive = true;
          break;
        }
      }

      return hasThreeInSeven || hasThreeConsecutive;
    },

    getCooldown: function (cooldown) {
      return cooldown * DAY;
    },

    getCooldowns: function () {
      const ds = newsletterWrapper.dataset;
      return {
        initial: this.getCooldown(ds.initialCooldown),
        second: this.getCooldown(ds.secondCooldown),
        last: this.getCooldown(ds.lastCooldown),
      };
    },

    getCooldownCondition: function () {
      const state = this.getState();
      if (!state.lastDateOpened || !state.cooldown) return true;
      return Date.now() - state.lastDateOpened >= state.cooldown;
    },

    setAttempts: function () {
      const state = this.getState();
      const cooldowns = this.getCooldowns();
      let attempts = (state.attempts || 0) + 1;

      let nextCooldown = cooldowns.initial;
      if (attempts === 2 || attempts === 3) nextCooldown = cooldowns.second;
      else if (attempts >= 4) nextCooldown = cooldowns.last;

      this.setState("attempts", attempts);
      this.setState("cooldown", nextCooldown);
      this.setState("lastDateOpened", Date.now());
    },

    getOpenCondition: async function (pageCount) {
      if (userInfo.IsSubscribed) {
        return;
      }

      const visitRequirements = this.checkVisitRequirements();

      const showModal = () => {
        isProcessing = true; // Lock to prevent multi-firing
        modalElement.classList.add("show");
        document.body.classList.add("modal-open");
        clearTimeout(timer);
      };

      if (visitRequirements) {
        resetAllConditions();
        showModal();
      }

      if (isProcessing || modalElement.classList.contains("show")) return;

      if (!this.getCooldownCondition()) return;

      const windowHeight = document.body.scrollHeight;

      if (pageCount <= 1) {
        // PAGE 1: 50% scroll AND 20 seconds
        if (timeIsLeft && window.pageYOffset > windowHeight * 0.5) {
          showModal();
        }
      } else {
        const scrollLimitMet = window.pageYOffset > windowHeight * 0.4;

        // PAGE 2+: 40% scroll OR 15 seconds
        if (scrollLimitMet || timeIsLeft) {
          showModal();
        }
      }
    },
  };

  const closeModal = (e) => {
    if (!modalElement.classList.contains("show")) return;

    if (
      e.target.closest(".dialog__close") ||
      !e.target.closest(".customized-newsletter")
    ) {
      document.body.classList.remove("modal-open");
      // Check if modal is actually open before setting attempts
      if (modalElement.classList.contains("show")) {
        modalElement.classList.remove("show");
        // --- MOVED HERE: Logic elevates only after modal is closing ---
        Config.setAttempts();
      }
    }
  };

  const triggerModalCheck = () => {
    // If already showing or processing, do nothing
    if (isProcessing || modalElement.classList.contains("show")) return;

    Config.getOpenCondition(pageCount);
  };

  const currentState = Config.getState();
  const pageCount = (currentState.pageCount || 0) + 1;
  Config.setState("pageCount", pageCount);

  let timeIsLeft = false;

  const waitTime =
    pageCount > 1 ? Config.timeToShowOnNextPage : Config.timeToShow;
  const timer = setTimeout(() => {
    timeIsLeft = true;

    triggerModalCheck();
  }, waitTime);

  // Close Logic
  document.addEventListener("click", closeModal);

  // Run record on load
  Config.recordVisit();

  window.addEventListener("scroll", triggerModalCheck, { passive: true });
}

loadNewsletterModal();
